import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';

import { PastConferencesContent } from '@/components/ui/features/events-list/EventListContent/PastConferencesContent';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { EventListStatus } from '@/enums/EventListStatus';
import { EventSort } from '@/enums/EventSort';
import { queryFeaturedThinEvent, queryThinEventsList } from '@/queries/thin-events';
import { ThinEvent } from '@/resources/ThinEvent';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';
import { StatusStringParam, StringParam, urlQueryStringValidate } from '@/utils/url-query-string';

const PastConferences: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Hydrate fallback={fallback}>
      <NextSeo
        title="Past scientific conferences"
        // eslint-disable-next-line max-len
        description="Find video lectures from your field's conferences worldwide. Underline's collection of conferences is always growing."
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/coreEvents`,
          title: 'Past scientific conferences',
          description:
            // eslint-disable-next-line max-len
            "Find video lectures from your field's conferences worldwide. Underline's collection of conferences is always growing.",
        }}
      />
      <PastConferencesContent />
    </Hydrate>
  );
};

interface IUrlQueryString {
  query: string;
  subjectId: string;
  conferenceId: string;
  sort: EventSort;
}
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { subjectId, conferenceId, sort, query } = urlQueryStringValidate<IUrlQueryString>(context.query, {
    query: StringParam,
    subjectId: StringParam,
    conferenceId: StringParam,
    status: StatusStringParam,
    sort: StringParam,
  });

  const store = generateStore({ context });

  const filters = {
    subject_id: subjectId,
    conference_id: conferenceId,
    query,
    scope: EventListStatus.past,
  };

  try {
    await Promise.all([
      store.fetchResourcesInfinite(queryThinEventsList(filters, sort)),
      store.fetchResources(queryFeaturedThinEvent()),
    ]);
  } catch (err) {
    return {
      props: {
        fallback: store.fallback,
        accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
      },
    };
  }

  return {
    props: {
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};

export default PastConferences;
